/**
 * Cinematic Viewport Navigation
 *
 * Creates a smooth "camera flight" effect when navigating to canvas elements.
 * Three-phase animation: zoom out → pan → zoom in
 */

// Easing function: ease-in-out-cubic for smooth acceleration/deceleration
function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

// Linear interpolation
function lerp(start: number, end: number, t: number): number {
  return start + (end - start) * t;
}

interface ViewportLike {
  centerX: number;
  centerY: number;
  zoom: number;
  setCenter: (x: number, y: number) => void;
  setZoom: (zoom: number, focusPoint?: { x: number; y: number }) => void;
}

interface CinematicOptions {
  /** Total animation duration in ms (default: 700) */
  duration?: number;
  /** Minimum zoom level during flight (default: 0.3) */
  minZoom?: number;
  /** Target zoom level at destination (default: 1.0) */
  targetZoom?: number;
}

/**
 * Cinematically navigate viewport to a target position with zoom-out-pan-zoom-in effect.
 *
 * @param viewport - The GFX viewport instance
 * @param targetX - Target center X coordinate
 * @param targetY - Target center Y coordinate
 * @param options - Animation options
 */
export function cinematicNavigateTo(
  viewport: ViewportLike,
  targetX: number,
  targetY: number,
  options: CinematicOptions = {}
): Promise<void> {
  const { duration = 1000, minZoom = 0.75, targetZoom = 1.0 } = options;

  return new Promise(resolve => {
    const startX = viewport.centerX;
    const startY = viewport.centerY;
    const startZoom = viewport.zoom;

    // Calculate distance to determine if we need the full cinematic effect
    const distance = Math.sqrt(
      Math.pow(targetX - startX, 2) + Math.pow(targetY - startY, 2)
    );

    // For short distances, use simpler animation
    const isShortDistance = distance < 500;

    // Calculate the zoom-out level based on distance
    // Subtle zoom-out: only reduce to 85% of current zoom, capped at minZoom
    const zoomOutLevel = isShortDistance
      ? Math.min(startZoom, targetZoom)
      : Math.max(minZoom, Math.min(startZoom, targetZoom) * 0.85);

    const startTime = performance.now();
    let animationFrame: number;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const rawProgress = Math.min(elapsed / duration, 1);

      // Apply easing
      const progress = easeInOutCubic(rawProgress);

      // Cinematic three-phase zoom curve (1000ms total):
      // Phase 1 (0-0.35): Smooth zoom out (350ms)
      // Phase 2 (0.35-0.65): Pan at zoomed-out level (300ms)
      // Phase 3 (0.65-1.0): Smooth zoom in (350ms)
      let currentZoom: number;

      if (isShortDistance) {
        // Simple zoom for short distances
        currentZoom = lerp(startZoom, targetZoom, progress);
      } else {
        // Cinematic 3-phase zoom: 35% out, 30% pan, 35% in
        if (progress < 0.35) {
          // Phase 1: Smooth zoom out (350ms)
          const phaseProgress = progress / 0.35;
          currentZoom = lerp(
            startZoom,
            zoomOutLevel,
            easeInOutCubic(phaseProgress)
          );
        } else if (progress < 0.65) {
          // Phase 2: Pan at zoomed-out level (300ms)
          currentZoom = zoomOutLevel;
        } else {
          // Phase 3: Smooth zoom in (350ms)
          const phaseProgress = (progress - 0.65) / 0.35;
          currentZoom = lerp(
            zoomOutLevel,
            targetZoom,
            easeInOutCubic(phaseProgress)
          );
        }
      }

      // Pan position follows main progress curve
      const currentX = lerp(startX, targetX, progress);
      const currentY = lerp(startY, targetY, progress);

      // Apply to viewport
      viewport.setCenter(currentX, currentY);
      viewport.setZoom(currentZoom, { x: currentX, y: currentY });

      if (rawProgress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        // Ensure we land exactly on target
        viewport.setCenter(targetX, targetY);
        viewport.setZoom(targetZoom, { x: targetX, y: targetY });
        resolve();
      }
    };

    animationFrame = requestAnimationFrame(animate);

    // Cleanup on unmount (if needed)
    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  });
}
